import { Edge, getIncomers, getOutgoers, Node } from "@xyflow/react";

/**
 * Get all nodes and edges related to the given nodes.
 */
export const getRelatedNodesAndEdges = (
  nodes: Node[],
  allNodes: Node[],
  allEdges: Edge[],
  getNode: (id: string) => Node | undefined,
) => {
  const relatedNodes = nodes.reduce((acc, node) => {
    return new Map([
      ...acc,
      ...new Map([
        [node.id, node],
        ...getAllIncomers(node, allNodes, allEdges, getNode),
        ...getAllOutgoers(node, allNodes, allEdges, getNode),
      ]),
    ]);
  }, new Map());

  const relatedEdges = allEdges
    .filter((edge) => relatedNodes.has(edge.target))
    .filter((edge) => relatedNodes.has(edge.source));

  return {
    nodes: relatedNodes,
    edges: relatedEdges,
  };
};

/**
 * Get all nodes that are connected to the given node via incoming edges.
 *
 * If a node has a parent, this will also include all incomers of the parent.
 */
const getAllIncomers = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  getNode: (id: string) => Node | undefined,
): Map<string, Node> => {
  const incomers = getIncomers(node, nodes, edges).reduce<Map<string, Node>>((acc, incomer) => {
    return new Map([
      ...acc,
      [incomer.id, incomer],
      ...getAllIncomers(incomer, nodes, edges, getNode),
    ]);
  }, new Map());

  let parentIncomers = new Map<string, Node>();
  if (node.parentId) {
    const parentId = getNode(node.parentId);
    if (parentId) {
      parentIncomers = getIncomers(parentId, nodes, edges).reduce<Map<string, Node>>(
        (acc, incomer) => {
          return new Map([
            ...acc,
            [incomer.id, incomer],
            ...getAllIncomers(incomer, nodes, edges, getNode),
          ]);
        },
        new Map([[parentId.id, parentId]]),
      );
    }
  }

  return new Map([...parentIncomers, ...incomers]);
};

/**
 * Get all nodes that are connected to the given node via outgoing edges.
 *
 * If a node has a parent, this will also include all outgoers of the parent.
 */
const getAllOutgoers = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  getNode: (id: string) => Node | undefined,
): Map<string, Node> => {
  const outgoers = getOutgoers(node, nodes, edges).reduce<Map<string, Node>>((acc, outgoer) => {
    return new Map([
      ...acc,
      [outgoer.id, outgoer],
      ...getAllOutgoers(outgoer, nodes, edges, getNode),
    ]);
  }, new Map());

  let parentOutgoers = new Map<string, Node>();
  if (node.parentId) {
    const parentId = getNode(node.parentId);
    if (parentId) {
      parentOutgoers = getOutgoers(parentId, nodes, edges).reduce<Map<string, Node>>(
        (acc, outgoer) => {
          return new Map([
            ...acc,
            [outgoer.id, outgoer],
            ...getAllOutgoers(outgoer, nodes, edges, getNode),
          ]);
        },
        new Map([[parentId.id, parentId]]),
      );
    }
  }

  return new Map([...parentOutgoers, ...outgoers]);
};
